
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../home/components/Header';
import { Footer } from '../home/components/Footer';
import { PricingCard } from './components/PricingCard';
import { loadStripe } from '@stripe/stripe-js';

// Preload Stripe for faster first click
const stripePromise = loadStripe(import.meta.env.VITE_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

// Enhanced checkout system with multiple fallback strategies
let isProcessing = false;
let currentCheckoutUrl: string | null = null;

// UI Helper Functions
function showOverlay(msg: string) {
  const overlay = document.getElementById('pay-overlay');
  if (overlay) {
    overlay.style.display = 'flex';
    const msgEl = overlay.querySelector('[data-msg]');
    if (msgEl) msgEl.textContent = msg;
  }
}

function hideOverlay() {
  const overlay = document.getElementById('pay-overlay');
  if (overlay) overlay.style.display = 'none';
}

function showError(msg: string) {
  hideOverlay();
  const errorEl = document.getElementById('pay-error');
  if (errorEl) {
    errorEl.textContent = msg;
    errorEl.classList.remove('hidden');
  }
}

function updateManualButton(url: string | null, enabled: boolean = true) {
  const button = document.getElementById('manual-checkout-btn') as HTMLButtonElement;
  if (button) {
    button.disabled = !enabled;
    button.dataset.url = url || '';
    button.classList.toggle('opacity-50', !enabled);
    button.classList.toggle('pointer-events-none', !enabled);
    button.textContent = enabled && url ? 'Continue to Secure Checkout' : 'Loading...';
  }
}

// Enhanced session creation with better error handling
async function createCheckoutSession(product: 'full' | 'plan'): Promise<{ sessionId: string; url: string }> {
  console.log('Creating checkout session for:', product);
  
  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  
  if (!supabaseUrl || !anonKey) {
    throw new Error('Payment system configuration missing');
  }
  
  const functionUrl = `${supabaseUrl}/functions/v1/create-checkout-session`;
  
  // Enhanced fetch with timeout and retry
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
  
  try {
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({ product }),
      signal: controller.signal,
      mode: 'cors'
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Session creation failed:', response.status, errorText);
      throw new Error(`Payment system error (${response.status}). Please try again.`);
    }
    
    const result = await response.json();
    console.log('Session created:', result);
    
    if (!result.success || !result.sessionId || !result.url) {
      throw new Error(result.message || result.error || 'Invalid session response');
    }
    
    // Validate session format
    if (!result.sessionId.startsWith('cs_live_')) {
      throw new Error('Payment system configuration error');
    }
    
    if (!result.url.startsWith('https://checkout.stripe.com/')) {
      throw new Error('Invalid checkout URL format');
    }
    
    return {
      sessionId: result.sessionId,
      url: result.url
    };
    
  } catch (error: any) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please check your connection and try again.');
    }
    
    throw error;
  }
}

// Bulletproof redirect system with multiple strategies
async function performCheckoutRedirect(sessionId: string, checkoutUrl: string): Promise<void> {
  console.log('Starting checkout redirect:', { sessionId, checkoutUrl });
  
  // Store URL for manual fallback
  currentCheckoutUrl = checkoutUrl;
  updateManualButton(checkoutUrl, true);
  
  // Strategy 1: Stripe.js redirect (primary)
  try {
    showOverlay('Redirecting to secure checkout...');
    
    const stripe = await stripePromise;
    if (!stripe) {
      throw new Error('Stripe failed to load');
    }
    
    console.log('Attempting Stripe.js redirect...');
    const { error } = await stripe.redirectToCheckout({ sessionId });
    
    if (error) {
      console.error('Stripe.js redirect failed:', error);
      throw error;
    }
    
    // If we reach here, redirect should have happened
    console.log('Stripe.js redirect initiated');
    
  } catch (stripeError) {
    console.error('Stripe.js redirect failed:', stripeError);
    
    // Strategy 2: Direct URL redirect (fallback)
    console.log('Using direct URL redirect fallback...');
    showOverlay('Using backup redirect method...');
    
    try {
      // Give a moment for the overlay to show
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Use window.location.assign for better compatibility
      window.location.assign(checkoutUrl);
      
      // If we're still here after 3 seconds, something went wrong
      setTimeout(() => {
        console.error('Direct redirect may have failed');
        showOverlay('If redirect doesn\'t work, click the button below');
      }, 3000);
      
    } catch (directError) {
      console.error('Direct redirect failed:', directError);
      
      // Strategy 3: Manual button (last resort)
      showOverlay('Please click the button below to continue');
      throw new Error('Automatic redirect failed. Please use the manual button.');
    }
  }
}

// Main checkout function
async function startSecureCheckout(product: 'full' | 'plan'): Promise<void> {
  if (isProcessing) {
    console.log('Already processing checkout');
    return;
  }
  
  isProcessing = true;
  
  try {
    // Clear any previous errors
    document.getElementById('pay-error')?.classList.add('hidden');
    
    // Show initial loading
    showOverlay('Creating secure checkout session...');
    updateManualButton(null, false);
    
    // Check network connectivity
    if (!navigator.onLine) {
      throw new Error('You appear to be offline. Please check your internet connection.');
    }
    
    // Create checkout session
    const { sessionId, url } = await createCheckoutSession(product);
    console.log('Session created successfully:', { sessionId, url });
    
    // Store in session storage for recovery
    try {
      sessionStorage.setItem('checkoutUrl', url);
      sessionStorage.setItem('sessionId', sessionId);
    } catch (e) {
      console.warn('Failed to store in sessionStorage:', e);
    }
    
    // Perform redirect with fallbacks
    await performCheckoutRedirect(sessionId, url);
    
  } catch (error: any) {
    console.error('Checkout failed:', error);
    isProcessing = false;
    
    showError(error.message || 'Checkout failed. Please try again.');
    
    // Enable manual button if we have a URL
    if (currentCheckoutUrl) {
      updateManualButton(currentCheckoutUrl, true);
    }
  }
}

export default function PurchasePage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'full' | 'payment' | 'done-with-you'>('full');
  const [searchParams] = useSearchParams();

  // Handle checkout with enhanced error handling
  const handleCheckout = async (product: 'full' | 'payment' | 'done-with-you') => {
    if (loading || isProcessing) {
      console.log('Already processing, ignoring click');
      return;
    }

    // Redirect to Teachable for all products
    if (product === 'full') {
      window.open('https://michael-jackson-s-school7.teachable.com/l/pdp/georgia-dealer-license', '_blank');
      return;
    }
    
    if (product === 'done-with-you') {
      window.open('https://michael-jackson-s-school7.teachable.com/l/coaching/991675/donewithyou-package', '_blank');
      return;
    }

    setLoading(product);
    setError(null);

    try {
      // Map product types
      const stripeProduct = product === 'payment' ? 'plan' : 'full';
      
      await startSecureCheckout(stripeProduct);

    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Checkout failed. Please try again.');
      
    } finally {
      setLoading(null);
      // Reset processing flag after a delay
      setTimeout(() => {
        isProcessing = false;
      }, 2000);
    }
  };

  // Manual checkout button handler
  const handleManualCheckout = () => {
    const button = document.getElementById('manual-checkout-btn') as HTMLButtonElement;
    const url = button?.dataset.url || currentCheckoutUrl || sessionStorage.getItem('checkoutUrl');
    
    if (!url) {
      showError('No checkout URL available. Please try creating a new session.');
      return;
    }
    
    console.log('Manual redirect to:', url);
    
    try {
      window.location.assign(url);
    } catch (error) {
      console.error('Manual redirect failed:', error);
      // Last resort: try window.open
      window.open(url, '_self');
    }
  };

  useEffect(() => {
    // Handle URL parameters
    const packageParam = searchParams.get('package');
    if (packageParam === 'done-with-you') {
      setSelectedPlan('done-with-you');
    }
    
    const canceledParam = searchParams.get('canceled');
    if (canceledParam === '1') {
      showCancelMessage();
    }

    // Set up manual button handler
    const setupManualButton = () => {
      const button = document.getElementById('manual-checkout-btn');
      if (button && !(button as any).__handlerAttached) {
        button.addEventListener('click', handleManualCheckout);
        (button as any).__handlerAttached = true;
      }
    };

    // Setup with delay to ensure DOM is ready
    setTimeout(setupManualButton, 100);

    // Try to restore checkout URL from storage
    const storedUrl = sessionStorage.getItem('checkoutUrl');
    if (storedUrl && storedUrl.startsWith('https://checkout.stripe.com/')) {
      currentCheckoutUrl = storedUrl;
      updateManualButton(storedUrl, true);
    }

  }, [searchParams]);

  const showCancelMessage = () => {
    const cancelDiv = document.createElement('div');
    cancelDiv.className = 'fixed top-4 left-4 right-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl shadow-lg z-50 p-4 max-w-md mx-auto';
    cancelDiv.innerHTML = `
      <div class="flex items-start">
        <div class="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
          <i class="ri-information-line text-yellow-600"></i>
        </div>
        <div class="flex-1">
          <h4 class="font-bold text-yellow-900 mb-2">Payment Cancelled</h4>
          <p class="text-yellow-700 text-sm mb-3">Your payment was cancelled. You can try again anytime.</p>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" 
                class="text-yellow-500 hover:text-yellow-700 ml-2 flex-shrink-0">
          <i class="ri-close-line"></i>
        </button>
      </div>
    `;
    document.body.appendChild(cancelDiv);
    setTimeout(() => cancelDiv.remove(), 8000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Enhanced Payment Overlay */}
      <div id="pay-overlay" style={{ display: 'none' }} className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="rounded-3xl bg-white p-10 text-center shadow-2xl max-w-md mx-4 border-2 border-gray-100">
          <div className="mb-8 animate-spin rounded-full h-20 w-20 border-b-4 border-teal-600 mx-auto"></div>
          <div className="mb-4 font-bold text-gray-900 text-xl">Processing Payment</div>
          <div data-msg className="text-gray-600 mb-8 leading-relaxed text-lg">Creating secure checkout session...</div>
          <button 
            id="manual-checkout-btn"
            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none text-lg shadow-lg hover:shadow-xl"
            data-url=""
            type="button"
            disabled
          >
            Loading...
          </button>
          <p className="text-sm text-gray-500 mt-4 leading-relaxed">
            If automatic redirect doesn't work, use the button above
          </p>
        </div>
      </div>

      {/* Enhanced Payment Error */}
      <div id="pay-error" className="hidden fixed top-4 left-4 right-4 z-[60] max-w-lg mx-auto bg-red-50 border-2 border-red-200 rounded-xl p-6 text-red-700 shadow-xl">
        <div className="flex items-start">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
            <i class="ri-error-warning-line text-red-600"></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold mb-2">Payment Error</div>
            <div className="text-sm leading-relaxed break-words"></div>
          </div>
          <button
            onClick={() => document.getElementById('pay-error')?.classList.add('hidden')}
            className="text-red-400 hover:text-red-600 ml-2 flex-shrink-0"
          >
            <i class="ri-close-line text-lg"></i>
          </button>
        </div>
      </div>

      <Header />
      
      <main className="container mx-auto px-4 py-8 md:px-6 md:py-12 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Error Display */}
          {error && (
            <div className="max-w-2xl mx-auto mb-8 bg-red-50 border-2 border-red-200 rounded-xl p-6 relative z-20">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-red-900 mb-3 text-lg">Payment Error</h4>
                  <p className="text-red-700 leading-relaxed break-words">{error}</p>
                  <div className="mt-4 text-sm text-red-600">
                    <p>If this problem persists, please contact support.</p>
                  </div>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0 p-1"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
          )}

          {/* Header Section */}
          <div className="text-center relative z-10">
            <div className="mb-16 sm:mb-20 md:mb-24 lg:mb-28">
              <div className="space-y-8 sm:space-y-10 md:space-y-12 lg:space-y-16">
                <h1 className="font-bold text-gray-900">
                  {selectedPlan === 'done-with-you' ? (
                    <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight">
                      Done-With-You
                    </div>
                  ) : (
                    <div className="space-y-4 sm:space-y-6 md:space-y-8">
                      <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight">
                        Your Georgia Dealer
                      </div>
                      <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight">
                        License
                      </div>
                    </div>
                  )}
                </h1>
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-10 shadow-lg">
                    <p className="text-lg sm:text-xl md:text-2xl text-gray-700 leading-relaxed font-medium">
                      {selectedPlan === 'done-with-you' 
                        ? 'Complete hand-holding through your entire licensing journey with expert guidance every step of the way'
                        : 'Join hundreds of successful entrepreneurs who have obtained their Georgia used car license with our proven system'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Cards Section */}
          <div className="mb-16 sm:mb-20 md:mb-24">
            {selectedPlan === 'done-with-you' ? (
              <div className="max-w-2xl mx-auto px-4 sm:px-6">
                <PricingCard
                  title="One-on-One Consulting Call"
                  price="$497"
                  originalPrice="$997"
                  description="Complete hand-holding through your entire licensing journey"
                  features={[
                    '3 one-hour sessions over 90 days',
                    'Application preparation assistance',
                    'Document review and feedback',
                    'Location scouting guidance',
                    'Bond and insurance shopping help',
                    'Unlimited email support',
                    'Priority scheduling',
                    'Complete 10-module course included',
                    'All templates and resources',
                    'Personal success manager'
                  ]}
                  isPopular={true}
                  onSelect={() => handleCheckout('done-with-you')}
                  isProcessing={!!loading || isProcessing}
                  loading={loading === 'done-with-you'}
                />
                
                <div className="text-center mt-12 sm:mt-16">
                  <button
                    onClick={() => setSelectedPlan('full')}
                    className="text-gray-600 hover:text-gray-800 underline text-lg cursor-pointer py-4 px-8 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:shadow-lg transition-all whitespace-nowrap"
                  >
                    ← Back to course options
                  </button>
                </div>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto px-4 sm:px-6">
                <PricingCard
                  title="Complete Course"
                  price="$97"
                  originalPrice="$197"
                  description="One-time payment - Best Value!"
                  features={[
                    'Complete 10-module course',
                    'All video lessons & resources',
                    'Downloadable checklists & templates',
                    'Official completion certificate',
                    'Lifetime access to updates',
                    'Email support included'
                  ]}
                  isPopular={true}
                  onSelect={() => handleCheckout('full')}
                  isProcessing={!!loading || isProcessing}
                  loading={loading === 'full'}
                />
              </div>
            )}
          </div>

          {/* One-on-One Consulting CTA Section */}
          {selectedPlan !== 'done-with-you' && (
            <div className="mb-16 sm:mb-20 md:mb-24 mx-4 sm:mx-6">
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-3xl p-8 sm:p-10 md:p-12 text-center shadow-xl">
                <div className="max-w-4xl mx-auto">
                  <div className="space-y-6 sm:space-y-8 md:space-y-10">
                    <h3 className="text-2xl sm:3xl md:4xl font-bold text-gray-900 leading-tight">
                      Want Complete Hand-Holding?
                    </h3>
                    
                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 sm:p-8">
                      <p className="text-lg sm:text-xl md:text-2xl text-gray-700 leading-relaxed font-medium">
                        Get personal guidance through every step of your licensing journey with our One-on-One Consulting Call
                      </p>
                    </div>
                    
                    <button
                      onClick={() => setSelectedPlan('done-with-you')}
                      className="bg-purple-600 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-semibold hover:bg-purple-700 transition-all duration-300 whitespace-nowrap cursor-pointer text-lg sm:text-xl shadow-lg hover:shadow-xl hover:scale-105"
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}